import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import { ArrowLeft, TrendingUp, Users, PoundSterling, Calendar, Award } from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Event } from '@/types';
import { formatCurrency } from '@/lib/utils';
import { countAttendeeRsvps, mapActiveRsvpsFromDocs } from '@/lib/event-utils';

const COLORS = {
  navy: '#1E3A5F',
  gold: '#D4AF37',
  green: '#10B981',
};

interface UnitStats {
  totalEvents: number;
  totalAttendees: number;
  totalRevenue: number;
  averageAttendance: number;
  eventTrends: Array<{ name: string; attendees: number; revenue: number }>;
  topEvents: Array<{ name: string; attendees: number }>;
  monthlyRevenue: Array<{ month: string; revenue: number }>;
}

export default function UnitAnalyticsPage() {
  const { unitId } = useParams<{ unitId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<UnitStats | null>(null);

  useEffect(() => {
    const fetchUnitStats = async () => {
      if (!unitId) {
        setLoading(false);
        return;
      }

      try {
        // Fetch all events for this unit
        const eventsSnapshot = await getDocs(
          query(
            collection(db, 'units', unitId, 'events'),
            orderBy('date', 'desc'),
            limit(12)
          )
        );

        const events: Event[] = [];
        eventsSnapshot.forEach((doc) => {
          const data = doc.data();
          const eventDate = data.date?.toDate() || new Date();
          events.push({
            id: doc.id,
            unitId,
            name: data.name,
            date: eventDate,
            location: data.location,
            costPerPerson: data.costPerPerson,
            questionnaire: data.questionnaire || [],
            currentAttendees: data.currentAttendees || 0,
            maxCapacity: data.maxCapacity,
            waitlistEnabled: data.waitlistEnabled || false,
            waitlist: data.waitlist || [],
            rsvpDeadline: data.rsvpDeadline?.toDate() || new Date(),
            paymentDeadline: data.paymentDeadline?.toDate() || eventDate,
            eventStatus: data.eventStatus || 'draft',
            dresscode: data.dresscode,
            customInstructions: data.customInstructions,
            isPublic: data.isPublic ?? false,
            requirePrefixRankUnitInfo: data.requirePrefixRankUnitInfo ?? true,
            createdBy: data.createdBy,
            createdAt: data.createdAt?.toDate() || new Date(),
          });
        });

        // Fetch RSVPs and invoices for each event
        let totalAttendees = 0;
        let totalRevenue = 0;
        const eventTrends: Array<{ name: string; attendees: number; revenue: number }> = [];
        const monthlyRevenueMap = new Map<string, number>();

        for (const event of events) {
          // Get RSVPs
          const rsvpsSnapshot = await getDocs(
            collection(db, 'units', unitId, 'events', event.id, 'rsvps')
          );
          const attendeeCount = countAttendeeRsvps(mapActiveRsvpsFromDocs(rsvpsSnapshot.docs));
          totalAttendees += attendeeCount;

          // Get invoices
          const invoicesSnapshot = await getDocs(
            collection(db, 'units', unitId, 'events', event.id, 'invoices')
          );
          let eventRevenue = 0;
          invoicesSnapshot.forEach((doc) => {
            const invoice = doc.data();
            if (invoice.deletedAt) {
              return;
            }
            eventRevenue += invoice.amountPaid || 0;
          });
          totalRevenue += eventRevenue;

          // Add to event trends
          eventTrends.push({
            name: event.name.length > 20 ? event.name.substring(0, 20) + '...' : event.name,
            attendees: attendeeCount,
            revenue: eventRevenue,
          });

          // Add to monthly revenue
          const monthKey = event.date.toLocaleDateString('en-GB', {
            year: 'numeric',
            month: 'short',
          });
          monthlyRevenueMap.set(
            monthKey,
            (monthlyRevenueMap.get(monthKey) || 0) + eventRevenue
          );
        }

        // Sort event trends by date (newest first)
        eventTrends.reverse();

        // Top events by attendance
        const topEvents = [...eventTrends]
          .sort((a, b) => b.attendees - a.attendees)
          .slice(0, 5);

        // Monthly revenue
        const monthlyRevenue = Array.from(monthlyRevenueMap.entries())
          .map(([month, revenue]) => ({ month, revenue }))
          .reverse();

        setStats({
          totalEvents: events.length,
          totalAttendees,
          totalRevenue,
          averageAttendance: events.length > 0 ? totalAttendees / events.length : 0,
          eventTrends,
          topEvents,
          monthlyRevenue,
        });
      } catch (error) {
        console.error('Error fetching unit stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUnitStats();
  }, [unitId]);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading unit analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <p className="text-muted-foreground">No data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 px-4 py-8 bg-gray-50">
        <div className="container mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Button variant="ghost" onClick={() => navigate(`/unit/${unitId}/settings`)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Unit
            </Button>
            <h1 className="mt-2 text-3xl font-bold">Unit Performance</h1>
            <p className="text-muted-foreground">Overall statistics and trends</p>
          </div>

          {/* Summary Cards */}
          <div className="grid gap-6 md:grid-cols-4 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Events</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalEvents}</div>
                <p className="text-xs text-muted-foreground mt-1">Last 12 events</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Attendees</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalAttendees}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Avg {Math.round(stats.averageAttendance)} per event
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                <PoundSterling className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(stats.totalRevenue)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Avg {formatCurrency(stats.totalEvents > 0 ? stats.totalRevenue / stats.totalEvents : 0)}/event
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Growth Rate</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">+12%</div>
                <p className="text-xs text-muted-foreground mt-1">vs last period</p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid gap-6 md:grid-cols-2 mb-8">
            {/* Event Attendance Trends */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Event Attendance Trends
                </CardTitle>
                <CardDescription>Attendance across recent events</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={stats.eventTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="attendees"
                      stroke={COLORS.navy}
                      fill={COLORS.navy}
                      fillOpacity={0.6}
                      name="Attendees"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Revenue Trends */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PoundSterling className="h-5 w-5" />
                  Revenue Trends
                </CardTitle>
                <CardDescription>Revenue per event</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats.eventTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(value) => formatCurrency(value as number)} />
                    <Legend />
                    <Bar dataKey="revenue" fill={COLORS.green} name="Revenue (£)" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Top Events */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  Top Events by Attendance
                </CardTitle>
                <CardDescription>Best attended events</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats.topEvents} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={120} />
                    <Tooltip />
                    <Bar dataKey="attendees" fill={COLORS.gold} name="Attendees" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Monthly Revenue */}
            {stats.monthlyRevenue.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Monthly Revenue
                  </CardTitle>
                  <CardDescription>Revenue by month</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={stats.monthlyRevenue}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrency(value as number)} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="revenue"
                        stroke={COLORS.green}
                        strokeWidth={2}
                        name="Revenue (£)"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

